<script setup lang="ts">
import { useEventListener } from '@vueuse/core';
import { computed, type CSSProperties } from 'vue';

import { IconChevronDown, IconPreloader } from '@/shared/ui/icons';
import { OverlayTeleport } from '@/shared/ui/overlay';

import type { DropdownMenuItem, DropdownContentConfig } from './types';

import { useDropdown } from './composables/use-dropdown';

interface Props {
  label?: string;
  items?: DropdownMenuItem[];
  content?: DropdownContentConfig;
  width?: string;
  /** Жёсткий потолок высоты поповера. Превалирует над `content.maxHeight`. */
  maxHeight?: number;
  showChevron?: boolean;
  disabled?: boolean;
  /** Дополнительный класс на обёртку триггера (используется как floating-ui anchor). */
  triggerClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  items: undefined,
  content: undefined,
  width: 'auto',
  maxHeight: undefined,
  showChevron: true,
  disabled: false,
  triggerClass: '',
});

const resolvedMaxHeight = computed(() => props.maxHeight ?? props.content?.maxHeight);

const { isOpen, triggerRef, floatingRef, dropdownStyles, transformOrigin, toggle, close } = useDropdown({
  offset: props.content?.sideOffset ?? 8,
  align: props.content?.align,
  side: props.content?.side,
  flip: props.content?.flip,
  shift: props.content?.shift,
  maxHeight: resolvedMaxHeight.value,
});

useEventListener(typeof document === 'undefined' ? undefined : document, 'keydown', (event: KeyboardEvent) => {
  if (isOpen.value && event.key === 'Escape') {
    event.preventDefault();
    close();
  }
});

// triggerRef используется в template через ref="triggerRef"
void triggerRef.value;
void floatingRef.value;

const popoverStyle = computed<CSSProperties>(() => ({
  ...dropdownStyles.value,
  width: props.width,
  transformOrigin: transformOrigin.value,
}));

const handleItemClick = async (item: DropdownMenuItem) => {
  if (item.disabled || item.loading || !item.onClick) return;
  await item.onClick(item);
  close();
};
</script>

<template>
  <div>
    <!-- Автоматический триггер с label -->
    <button
      v-if="label"
      ref="triggerRef"
      class="flex items-center gap-2 rounded-lg bg-(--secondary-button-bg-color) px-4 py-2 text-(--base-popup-text-color) transition-colors duration-200 hover:bg-(--secondary-button-hover-bg-color)"
      :disabled="disabled"
      @click="toggle"
    >
      <span class="text-sm font-medium">{{ label }}</span>
      <IconChevronDown
        v-if="showChevron"
        class="transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        :width="10"
        :height="10"
      />
    </button>

    <!-- Кастомный триггер через slot -->
    <div
      v-else
      ref="triggerRef"
      :class="triggerClass"
    >
      <slot
        name="trigger"
        :is-open="isOpen"
        :toggle="toggle"
      />
    </div>

    <!-- Меню -->
    <OverlayTeleport>
      <!-- Overlay для закрытия меню при клике вне его -->
      <div
        v-if="isOpen"
        class="fixed inset-0 z-(--popup-z-index)"
        @click="close"
      />

      <!-- Выпадающее меню -->
      <Transition
        enter-active-class="transition-[opacity,scale] duration-[180ms] ease-snappy"
        enter-from-class="opacity-0 scale-[0.96] motion-reduce:scale-100"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition-opacity duration-100 ease-snappy"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isOpen"
          ref="floatingRef"
          class="base-dropdown-popup z-(--popup-z-index) overflow-y-auto rounded-lg border border-(--base-popup-border-color) bg-(--base-popup-bg) shadow-xl"
          :style="popoverStyle"
          @click.stop
        >
          <!-- Автоматический рендер items -->
          <div
            v-if="items"
            class="py-1"
          >
            <template
              v-for="(item, index) in items"
              :key="item.id || index"
            >
              <div
                v-if="item.divider"
                class="my-1 h-px bg-(--base-popup-border-color)"
              />
              <button
                v-else
                class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-(--base-popup-text-color) transition-colors duration-150 hover:bg-(--base-popup-hover-color)"
                :class="{
                  'cursor-not-allowed opacity-50': item.disabled || item.loading,
                }"
                :disabled="item.disabled || item.loading"
                @click="handleItemClick(item)"
              >
                <component
                  :is="item.icon"
                  v-if="item.icon && !item.loading"
                  :width="16"
                  :height="16"
                />
                <span>{{ item.label }}</span>
                <IconPreloader
                  v-if="item.loading"
                  class="ml-auto"
                  :width="16"
                  :height="16"
                />
              </button>
            </template>
          </div>

          <!-- Кастомный контент через slot -->
          <slot
            v-else
            name="content"
            :close="close"
          />
        </div>
      </Transition>
    </OverlayTeleport>
  </div>
</template>
